export type MessageRole = 'user' | 'assistant';

export interface MessageLink {
  text: string;
  url: string;
}

export interface MessageAction {
  id: string;
  label: string;
  type: 'tag' | 'status' | 'assign' | 'custom' | 'insert-link' | 'add-related';
  payload?: any;
}

export type ContentSourceType = 'semantic' | 'keyword' | 'both';

export type ContentType =
  | 'Article'
  | 'Product'
  | 'Event'
  | 'Publication'
  | 'Newsletter'
  | 'Page'
  | 'Directory'
  | 'Blog';

export interface SearchResultItem {
  id: string;
  title: string;
  /** Primary link (admin route when known, else the public path) */
  url: string;
  /** Public site path — used when inserting links into content */
  canonicalUrl?: string;
  /** Featured image URL, when the item has one */
  imageUrl?: string;
  publishedAt: string;
  authors: string[];
  taxonomies: string[];
  keywords: string[];
  score: number;
  matchReason: string;
  source: ContentSourceType;
  contentType: ContentType;
}

/** Content an assistant reply created, rendered as a card the editor opens. */
export interface CreatedContent {
  kind: 'article' | 'brief';
  /** Numeric article id */
  oid: string;
  title: string;
  /** Admin route to edit it */
  url: string;
  published: boolean;
  /** For a brief, the articles it was written from */
  sources: MessageLink[];
}

export interface Message {
  id: string;
  role: MessageRole;
  content: string;
  /** Content this reply created */
  created?: CreatedContent[];
  /** Rows checked on the article list when this was sent (article oids) */
  selectedArticleIds?: string[];
  /** The assistant's intermediate reasoning, shown collapsed */
  reasoning?: string[];
  /** Tools the assistant ran to answer, in order */
  toolsUsed?: string[];
  timestamp: Date | string;
  links?: MessageLink[];
  actions?: MessageAction[];
  searchResults?: SearchResultItem[];
  isLoading?: boolean;
}

export interface Chat {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date | string;
  updatedAt: Date | string;
}

// Page context types for contextual awareness
export type PageType = 
  | 'dashboard'
  | 'article-list'
  | 'article-edit'
  | 'page-list'
  | 'page-edit'
  | 'event-list'
  | 'event-edit'
  | 'user-list'
  | 'user-edit'
  | 'media'
  | 'directory-list'
  | 'directory-edit'
  | 'product-list'
  | 'product-edit'
  | 'order-list'
  | 'order-edit'
  | 'newsletter-list'
  | 'newsletter-edit'
  | 'publication-list'
  | 'publication-edit'
  | 'taxonomy'
  | 'workflow'
  | 'settings'
  | 'analytics'
  | 'unknown';

export interface EntityContext {
  id?: string | number;
  title?: string;
  status?: string;
  type?: string;
  [key: string]: any; // Allow additional entity-specific data
}

export interface PageContext {
  path: string;
  pageType: PageType;
  section?: string; // e.g., 'editorial', 'revenue', 'marketing', 'advanced'
  entity?: EntityContext;
  listFilters?: Record<string, any>; // Current filters on list pages
  selectedArticleIds?: string[]; // Rows checked on the article list (global ids)
  metadata?: Record<string, any>; // Any additional page-specific data
  /** The useAISearchPageContext call that set this, so only it clears it */
  owner?: string;
}

export interface AISearchState {
  chats: Chat[];
  activeChatId: string | null;
  isTyping: boolean;
  isDrawerOpen: boolean;
  pageContext: PageContext | null;
}

/**
 * Minimal interface for a Froala editor instance.
 * We only need the methods required for link insertion.
 */
/**
 * Minimal interface for a Froala editor instance.
 * We only need the methods required for link insertion.
 */
export interface FroalaEditorInstance {
  html: {
    insert: (html: string, clean?: boolean) => void;
    get: () => string;
  };
  selection: {
    save: () => void;
    restore: () => void;
  };
  undo: {
    saveStep: () => void;
  };
  events: {
    trigger: (name: string, args?: any[], force?: boolean) => void;
  };
}

/**
 * Global registry for the most recently focused Froala editor instance.
 * Froala plugins are inherently global (FroalaEditor.PLUGINS), so this
 * follows the same pattern. The `initialized` and `focus` events on
 * each editor update this reference.
 */
export const froalaEditorRegistry = {
  current: null as FroalaEditorInstance | null,
};
